/**
 * Composant principal du Dashboard
 * @module pages/Dashboard
 */

import React, { lazy, Suspense, useMemo, useCallback, useState, useEffect } from 'react';
import { isSpotifyConnectedForUi } from '../utils/spotifyConnectedUi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUserSession } from '../hooks/useUserSession';
import { usePremiumStatus } from '../hooks/usePremiumStatus';
import { useRewards } from '../hooks/UseRewards';
import { useSpotifyToken } from '../hooks/useSpotifyToken';
import NotificationService from '../services/NotificationService';
import Sidebar from '../components/Sidebar';
import '../styles/Dashboard.css';
import { STORAGE_KEYS, DASHBOARD_VALID_TABS } from '../config/constants';
import PremiumGate from '../components/premium/PremiumGate';
import {
  getPremiumFeatureIdForDashboardTab,
  DASHBOARD_TAB_PREMIUM_LABELS,
} from '../config/dashboardPremiumFeatures';
import { usePublicConfig } from '../hooks/usePublicConfig';
import { storage } from '../utils/storage';
import { useTabNavigation } from './dashboard/hooks/useTabNavigation';
import { useSocketListeners } from './dashboard/hooks/useSocketListeners';
import { useRewardManagement } from './dashboard/hooks/useRewardManagement';
import { useBirthdayNotification } from '../hooks/useBirthdayNotification';
import LoadingFallback from './dashboard/components/LoadingFallback';
import TwitchAuthGate from '../components/auth/TwitchAuthGate';
import './dashboard/styles/loadingStyles.css';

// Lazy loading des composants pour améliorer les performances
const OverviewTab = lazy(() => import('../components/OverviewTab'));
const RewardsTab = lazy(() => import('../components/RewardsTab'));
const ParametersTab = lazy(() => import('../components/ParametersTab'));
const FollowedChannelsTab = lazy(() =>
  import('../components/FollowedChannelsTab')
);
const ChatMentionsTab = lazy(() => import('../components/ChatMentionsTab'));
const FollowsSubsTab = lazy(() => import('../components/FollowsSubsTab'));
const SpotifyQueueManager = lazy(() =>
  import('../components/SpotifyQueueManager/SpotifyQueueManager')
);
const StreamStatsTab = lazy(() => import('../components/StreamStatsTab'));
const BirthdaysTab = lazy(() => import('../components/BirthdaysTab'));
const ClipsTab = lazy(() => import('../components/ClipsTab'));
const OverlaysTab = lazy(() => import('../components/OverlaysTab'));
const CommandsTab = lazy(() => import('../components/CommandsTab'));
const AITab = lazy(() => import('../components/AITab'));

/**
 * Composant Dashboard principal
 * @returns {JSX.Element}
 */
function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    user,
    getTwitchUser,
    isTwitchConnected,
    isSpotifyConnected,
    connectTwitch,
    connectSpotify,
  } = useUserSession();
  const {
    isPremium,
    isTrial,
    eligibleForTrial,
    requestTrial,
    refreshPremiumStatus,
  } = usePremiumStatus();
  const { config: publicConfig } = usePublicConfig();
  const twitchUserData = useMemo(
    () => (getTwitchUser ? getTwitchUser() : null),
    [getTwitchUser]
  );

  // Récupération du token Spotify depuis le backend (sécurisé)
  const {
    spotifyToken,
    loading: spotifyTokenLoading,
    error: spotifyTokenError,
    isExpired: spotifyTokenExpired,
    refreshToken: refreshSpotifyToken,
  } = useSpotifyToken();

  const jwtSpotifyConnected = isSpotifyConnected();
  const spotifyConnectedForUi = useMemo(
    () =>
      isSpotifyConnectedForUi(jwtSpotifyConnected, {
        loading: spotifyTokenLoading,
        spotifyToken,
        isExpired: spotifyTokenExpired,
        error: spotifyTokenError,
      }),
    [
      jwtSpotifyConnected,
      spotifyTokenLoading,
      spotifyToken,
      spotifyTokenExpired,
      spotifyTokenError,
    ]
  );

  const [selectedRewardId, setSelectedRewardId] = useState(
    () => storage.getString(STORAGE_KEYS.SELECTED_REWARD_ID) || null
  );

  // Hooks personnalisés
  const { activeTab, isTransitioning, activeTabRef, switchTab } =
    useTabNavigation();

  /** Retour Stripe Checkout : toast + rafraîchissement du statut premium. */
  useEffect(() => {
    const premiumParam = searchParams.get('premium');
    if (premiumParam !== 'success' && premiumParam !== 'cancel') return;

    if (premiumParam === 'success') {
      NotificationService.success(
        'Paiement reçu. L’abonnement Premium s’active sous quelques instants.',
        { toastId: 'premium-checkout-success' }
      );
      refreshPremiumStatus();
    } else {
      NotificationService.info(
        'Paiement annulé. Votre compte n’a pas été modifié.',
        { toastId: 'premium-checkout-cancel' }
      );
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('premium');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, refreshPremiumStatus]);

  /** Deep-link ?tab= depuis les CTA Premium (ex. /dashboard?tab=overview#…) */
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (!tabParam) return;
    if (!DASHBOARD_VALID_TABS.includes(tabParam)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('tab');
          return next;
        },
        { replace: true }
      );
      return;
    }
    switchTab(tabParam);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('tab');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams, switchTab]);

  const onTwitchReauthRequired = useCallback(
    (userId) => {
      if (user?.id === userId) {
        connectTwitch();
      }
    },
    [user?.id, connectTwitch]
  );

  const {
    hasNewMentions,
    hasNewFollowedChannelOnline,
    resetMentionsBadge,
    resetFollowedBadge,
  } = useSocketListeners(activeTabRef, { onTwitchReauthRequired });

  const {
    rewards,
    loading: loadingRewards,
    error: errorRewards,
    setRewards,
  } = useRewards({
    twitchUser: twitchUserData,
    selectedRewardId,
    setSelectedRewardId,
  });

  const {
    showAddModal,
    setShowAddModal,
    handleRewardAdded,
    handleRewardUpdated,
    handleDeleteReward,
  } = useRewardManagement(rewards, setRewards);

  // Vérifier et notifier les anniversaires du jour
  useBirthdayNotification(true, 3600000); // Vérifie toutes les heures

  // Gestion du changement d'onglet avec réinitialisation des pastilles
  const handleSwitchTab = useCallback(
    (tab) => {
      switchTab(tab);

      // Réinitialiser la pastille si on clique sur l'onglet mentions
      if (tab === 'mentions') {
        resetMentionsBadge();
      }
      // Réinitialiser la pastille si on clique sur l'onglet des suivis
      if (tab === 'followed') {
        resetFollowedBadge();
      }
    },
    [switchTab, resetMentionsBadge, resetFollowedBadge]
  );

  // Fonction pour connecter Twitch et Spotify en une seule action
  const handleConnectAll = useCallback(() => {
    // Connecter Twitch si pas déjà connecté
    if (!isTwitchConnected()) {
      try {
        connectTwitch();
        return;
      } catch (error) {
        NotificationService.handleError(error, 'Dashboard - Connexion Twitch');
      }
    }

    // Si Twitch est connecté mais pas Spotify, connecter Spotify
    if (isTwitchConnected() && !spotifyConnectedForUi) {
      try {
        connectSpotify();
      } catch (error) {
        NotificationService.handleError(error, 'Dashboard - Connexion Spotify');
      }
    }
  }, [
    isTwitchConnected,
    spotifyConnectedForUi,
    connectTwitch,
    connectSpotify,
  ]);

  const wrapTabWithPremiumGate = useCallback((tabKey, inner) => {
    const featureId = getPremiumFeatureIdForDashboardTab(tabKey);
    if (!featureId) return inner;
    const featureName =
      DASHBOARD_TAB_PREMIUM_LABELS[tabKey] || 'Cette fonctionnalité';
    return (
      <PremiumGate featureId={featureId} featureName={featureName}>
        {inner}
      </PremiumGate>
    );
  }, []);

  // Mémoriser le contenu de l'onglet actif pour éviter les re-renders
  const tabContent = useMemo(() => {
    const contentClass = isTransitioning ? 'tab-content-exit' : 'tab-content';

    return (
      <div className={contentClass} key={activeTab}>
        {activeTab === 'overview' && <OverviewTab />}

        {activeTab === 'rewards' &&
          wrapTabWithPremiumGate(
            'rewards',
            <RewardsTab
              rewards={rewards}
              loading={loadingRewards}
              error={errorRewards}
              onAddClick={() => setShowAddModal(true)}
              showAddModal={showAddModal}
              onCloseAddModal={() => setShowAddModal(false)}
              onRewardAdded={handleRewardAdded}
              onDeleteReward={handleDeleteReward}
              onRewardUpdated={handleRewardUpdated}
              twitchUser={twitchUserData}
            />
          )}

        {activeTab === 'spotify' &&
          wrapTabWithPremiumGate(
            'spotify',
            <SpotifyQueueManager
              spotifyToken={spotifyToken}
              spotifyTokenLoading={spotifyTokenLoading}
              spotifyTokenError={spotifyTokenError}
              spotifyTokenExpired={spotifyTokenExpired}
              onRefreshToken={refreshSpotifyToken}
              onConnectSpotify={connectSpotify}
            />
          )}

        {activeTab === 'followed' &&
          wrapTabWithPremiumGate('followed', <FollowedChannelsTab />)}

        {activeTab === 'mentions' &&
          wrapTabWithPremiumGate('mentions', <ChatMentionsTab />)}

        {activeTab === 'followssubs' &&
          wrapTabWithPremiumGate('followssubs', <FollowsSubsTab />)}

        {activeTab === 'streamstats' &&
          wrapTabWithPremiumGate('streamstats', <StreamStatsTab />)}

        {activeTab === 'birthdays' &&
          wrapTabWithPremiumGate('birthdays', <BirthdaysTab />)}

        {activeTab === 'clips' &&
          wrapTabWithPremiumGate('clips', <ClipsTab />)}

        {activeTab === 'overlays' &&
          wrapTabWithPremiumGate('overlays', <OverlaysTab />)}

        {activeTab === 'commands' &&
          wrapTabWithPremiumGate('commands', <CommandsTab />)}

        {activeTab === 'ai' && wrapTabWithPremiumGate('ai', <AITab />)}

        {activeTab === 'parameters' &&
          wrapTabWithPremiumGate('parameters', <ParametersTab />)}
      </div>
    );
  }, [
    activeTab,
    isTransitioning,
    rewards,
    loadingRewards,
    errorRewards,
    showAddModal,
    setShowAddModal,
    handleRewardAdded,
    handleDeleteReward,
    handleRewardUpdated,
    twitchUserData,
    spotifyToken,
    spotifyTokenLoading,
    spotifyTokenError,
    spotifyTokenExpired,
    refreshSpotifyToken,
    connectSpotify,
    wrapTabWithPremiumGate,
  ]);

  return (
    <div className="dashboardPage">
      <Sidebar
        activeTab={activeTab}
        switchTab={handleSwitchTab}
        navigate={navigate}
        hasNewMentions={hasNewMentions}
        hasNewFollowedChannelOnline={hasNewFollowedChannelOnline}
        onConnectAll={handleConnectAll}
        isTwitchConnected={isTwitchConnected()}
        isSpotifyConnected={spotifyConnectedForUi}
        isPremium={isPremium}
        isTrial={isTrial}
        eligibleForTrial={eligibleForTrial}
        onRequestTrial={requestTrial}
        twitchUser={twitchUserData}
        user={user}
        premiumFeatureGating={publicConfig.premiumFeatureGating}
      />

      <main className="mainContent">
        <div className="dashboardContentSurface">
          <TwitchAuthGate twitchUser={twitchUserData} onConnect={connectTwitch}>
            <Suspense fallback={<LoadingFallback />}>{tabContent}</Suspense>
          </TwitchAuthGate>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
